import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { DialogTitle, DialogContent, Dialog, Button, DialogActions, Checkbox, FormControlLabel } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { validatePlant } from '@/helpers/administrationUtils';
import { SELECTION_MODES_FORM, WORKFLOW_TYPE_FORM } from '@/helpers/constants';
import { formatSummaryUser } from '@/helpers/utils';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { getLanguagesForm } from '@/redux/Forms/forms.reducer';
import administrationServices from '@/services/administrationServices';
import { LanguagesForm, UserSearch } from '@/types';
import styles from './DialogPlants.module.scss';
import Dropdown from '../UI/Dropdown/Dropdown';
import InputText from '../UI/InputText/InputText';
import InputUser from '../UI/InputUser/InputUser';
import UsersSelection from '../UI/UsersSelection/UsersSelection';

const requiredFields = ['plantName', 'wkzNumber', 'language', 'mailbox', 'selectionMode'];

const useStyles = makeStyles(() => ({
  content: {
    paddingTop: 0,
    width: '800px',
  },
  activeForm: {
    display: 'flex',
    flexDirection: 'row',
    height: '55px',
    width: '100%',
    justifyContent: 'flex-start',
  },
}));

const INITIAL_FORM_VALUE = {
  plantName: '',
  wkzNumber: '',
  authorizedUserCodes: [],
  language: '',
  mailbox: '',
  active: false,
  workflowType: 'NOT_READY',
  qmbApprover: '',
  selectionMode: '',
  ckd: false,
};

interface DialogPlantsProps {
  close: () => void;
  title: string;
  reloadPlants: () => void;
  languages: LanguagesForm;
}

const DialogPlants: React.FC<DialogPlantsProps> = (props) => {
  const { close, title, reloadPlants, languages } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const [fieldsCompleted, setFieldsCompleted] = useState(false);
  const [plant, setPlant] = useState(INITIAL_FORM_VALUE);
  const [userSelected, setUserSelected] = useState<UserSearch | null>(null);

  const handlePlantChange = (inputName: string, inputValue: string | string[] | null | boolean) => {
    const newState = { ...plant, [inputName]: inputValue };
    const fieldsOk = requiredFields.reduce((fieldOk, fieldToCheck) => (newState[fieldToCheck] !== '' ? fieldOk + 1 : fieldOk), 0);
    setFieldsCompleted(fieldsOk === requiredFields.length);
    setPlant(newState);
  };

  const savePlant = () => {
    if (validatePlant(plant, addToast, t)) {
      setLoading(true);
      administrationServices.createPlant(plant)
        .then(() => { reloadPlants(); })
        .catch((error) => addToast(error.message, 'ERROR'))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <div className={styles.DialogPlants}>
          <div className={styles.PageContent}>
            <div className={styles.Fields}>
              <InputText
                id="input-plant-name"
                value={plant.plantName}
                isMandatory
                label={t('plants.plantsName')}
                handleChange={(ev) => handlePlantChange('plantName', ev.target.value)}
              />
              <InputText
                id="input-wkz-number"
                type="number"
                value={plant.wkzNumber}
                isMandatory
                maxLength={4}
                label={t('plants.wkz')}
                handleChange={(ev) => handlePlantChange('wkzNumber', ev.target.value)}
              />
              <Dropdown
                id="dropdown-workflow-type"
                label={t('plants.workflowType')}
                isMandatory
                options={WORKFLOW_TYPE_FORM}
                value={plant.workflowType}
                handleChange={(ev) => handlePlantChange('workflowType', ev.target.value)}
                formControlClass={styles.DropdownYAxisAlign}
              />
              <InputText
                id="input-plant-email"
                value={plant.mailbox}
                isMandatory
                label={t('plants.mailbox')}
                maxLength={100}
                handleChange={(ev) => handlePlantChange('mailbox', ev.target.value)}
              />
              <Dropdown
                id="dropdown-language"
                label={t('plants.language')}
                isMandatory
                options={languages as unknown as { [key: string]: { [key: string]: string } }}
                value={plant.language}
                handleChange={(ev) => handlePlantChange('language', ev.target.value)}
                formControlClass={styles.DropdownYAxisAlign}
              />
              <Dropdown
                id="dropdown-selection-type"
                label={t('plants.selectionMode')}
                isMandatory
                options={SELECTION_MODES_FORM}
                value={plant.selectionMode}
                handleChange={(ev) => handlePlantChange('selectionMode', ev.target.value)}
                formControlClass={styles.DropdownYAxisAlign}
              />
              <div className="w-100">
                <InputUser
                  id="input-user-qmb-approver"
                  label={t('plants.qmbApprover')}
                  value={userSelected ? formatSummaryUser(userSelected) : ''}
                  code={plant.qmbApprover}
                  clear={() => { setUserSelected(null); handlePlantChange('qmbApprover', ''); }}
                  updateUserSelected={(user: UserSearch) => { setUserSelected(user); handlePlantChange('qmbApprover', user.userCode); }}
                />
              </div>
              <div className={styles.Checkboxes}>
                <FormControlLabel
                  classes={{ root: classes.activeForm }}
                  checked={plant.active}
                  control={(
                    <Checkbox
                      id="checkbox-plant-active"
                      data-testid="checkbox-plant-active"
                      color="primary"
                      onChange={(ev) => handlePlantChange('active', ev.target.checked)}
                    />
                  )}
                  label={t('plants.active')}
                  labelPlacement="top"
                />
                <FormControlLabel
                  classes={{ root: classes.activeForm }}
                  checked={plant.ckd}
                  control={(
                    <Checkbox
                      id="checkbox-plant-ckd"
                      data-testid="checkbox-plant-ckd"
                      color="primary"
                      onChange={(ev) => handlePlantChange('ckd', ev.target.checked)}
                    />
                  )}
                  label={t('plants.ckd')}
                  labelPlacement="top"
                />
              </div>
              <div className={styles.UsersAndActive}>
                <div className={styles.UsersSelect}>
                  <span className={styles.UsersSelectLabel}>{t('plants.authorizedUsers')}</span>
                  <UsersSelection
                    id="input-authorized-id-users"
                    value={plant.authorizedUserCodes}
                    onChange={(newUsers) => handlePlantChange('authorizedUserCodes', newUsers)}
                    displayInTable={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {!fieldsCompleted && (<div className={styles.FieldsMissing}>{t('plants.fieldsMissing')}</div>)}
        <Button
          id="button-create-plant"
          data-testid="button-create-plant"
          variant="contained"
          color="primary"
          onClick={savePlant}
          disabled={!fieldsCompleted}
        >
          {t('dialog.save')}
        </Button>
        <Button
          id="button-close-dialog-plant"
          data-testid="button-close-dialog-plant"
          color="primary"
          onClick={close}
        >
          {t('dialog.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  languages: getLanguagesForm(state),
});

export default connect(mapStateToProps)(DialogPlants);
